import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  Section, Inner, SectionHeader, SectionTitle, SectionSubtitle, Divider, Grid, Card, Badge,
} from '../styles/v2.styled';

/* ═══════════════════════════════════════════════
   WorkRoomPage — LMS 작업실 (통합)
     1) 워크스페이스 (작업관리·포트폴리오·일정관리)
     2) 직무별 작업실 (Developer/Designer/Infra/Modeler/Media/Plan)
     3) 스터디 그룹
     4) 직무별 추천 로드맵
   ═══════════════════════════════════════════════ */

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
`;

const PageWrapper = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

/* ── Hero ── */
const Hero = styled.section`
  padding: 88px 0 56px;
  background: linear-gradient(135deg, #0c2a32 0%, #144a55 50%, #0a8294 100%);
  color: #fff;
  text-align: center;
  position: relative;
  overflow: hidden;
`;
const HeroInner = styled.div`
  max-width: 760px; margin: 0 auto; padding: 0 24px;
  position: relative; z-index: 2;
`;
const HeroEyebrow = styled.div`
  display: inline-block; font-size: 0.75rem; font-weight: 800;
  letter-spacing: 3px; color: #67e8f9; margin-bottom: 14px;
`;
const HeroTitle = styled.h1`
  font-size: 2.6rem; font-weight: 900; margin: 0 0 14px; letter-spacing: -1px;
  @media (max-width: 640px) { font-size: 1.85rem; }
`;
const HeroDesc = styled.p`
  font-size: 1.05rem; opacity: 0.85; line-height: 1.65; margin: 0;
`;
const HeroOrb = styled.div`
  position: absolute; border-radius: 50%; pointer-events: none;
  background: radial-gradient(circle, rgba(103, 232, 249, 0.22), transparent 70%);
  animation: ${float} 6s ease-in-out infinite;
  ${({ $size }) => `width: ${$size}; height: ${$size};`}
  ${({ $top, $left, $right, $bottom }) => `
    ${$top !== undefined ? `top: ${$top};` : ''}
    ${$left !== undefined ? `left: ${$left};` : ''}
    ${$right !== undefined ? `right: ${$right};` : ''}
    ${$bottom !== undefined ? `bottom: ${$bottom};` : ''}
  `}
`;

/* ── Quick Tabs (앵커 네비) ── */
const Anchors = styled.div`
  background: #fff;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  position: sticky; top: 72px; z-index: 10;
`;
const AnchorsInner = styled.div`
  max-width: 1200px; margin: 0 auto; padding: 0 24px;
  display: flex; gap: 4px; overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;
const AnchorBtn = styled.button`
  background: none; border: 0; padding: 16px 18px;
  font-size: 0.92rem; font-weight: 800; cursor: pointer; white-space: nowrap;
  color: ${({ theme }) => theme.colors.neutral[600]};
  border-bottom: 3px solid transparent;
  transition: color 0.2s, border-color 0.2s;
  &:hover { color: ${({ theme }) => theme.colors.primary[700]}; border-bottom-color: ${({ theme }) => theme.colors.primary[300]}; }
`;

/* ── Workspace 메인 카드 (3개) ── */
const WorkspaceGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const WorkspaceCard = styled(Link)`
  display: flex; flex-direction: column; padding: 30px 28px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 14px;
  text-decoration: none; color: inherit;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  position: relative; overflow: hidden;

  &::before {
    content: ''; position: absolute; top: 0; left: 0;
    width: 100%; height: 4px;
    background: ${({ $color }) => $color};
  }
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
    border-color: ${({ $color }) => $color};
  }
`;
const WSIcon = styled.div`
  width: 52px; height: 52px; border-radius: 14px;
  background: ${({ $color }) => `${$color}1A`};
  color: ${({ $color }) => $color};
  display: flex; align-items: center; justify-content: center;
  font-size: 1.3rem; font-weight: 900; margin-bottom: 16px;
`;
const WSTitle = styled.div`
  font-size: 1.15rem; font-weight: 900; color: #0f172a; margin-bottom: 6px;
`;
const WSDesc = styled.p`
  font-size: 0.9rem; color: #64748b; line-height: 1.55; margin: 0 0 14px; flex: 1;
`;
const WSCta = styled.span`
  font-size: 0.86rem; font-weight: 800;
  color: ${({ $color }) => $color};
`;

/* ── Role 카드 (6개) ── */
const RoleGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;
const RoleCard = styled(Link)`
  display: flex; gap: 14px; padding: 20px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 12px;
  text-decoration: none; color: inherit;
  transition: all 0.2s;
  &:hover {
    border-color: ${({ $color }) => $color};
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);
  }
`;
const RoleIcon = styled.div`
  width: 44px; height: 44px; border-radius: 10px;
  background: ${({ $color }) => `${$color}1A`};
  color: ${({ $color }) => $color};
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; font-weight: 900; flex-shrink: 0;
`;
const RoleBody = styled.div`flex: 1;`;
const RoleTitle = styled.div`
  font-size: 1rem; font-weight: 800; color: #0f172a; margin-bottom: 2px;
`;
const RoleDesc = styled.div`
  font-size: 0.83rem; color: #64748b; line-height: 1.5;
`;

/* ── Workflow Steps ── */
const Workflow = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;
const StepCard = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  border-radius: 12px;
  padding: 22px 20px;
  position: relative;
`;
const StepNum = styled.div`
  font-size: 0.78rem; font-weight: 900;
  color: ${({ theme }) => theme.colors.primary[600]};
  letter-spacing: 2px; margin-bottom: 10px;
`;
const StepTitle = styled.div`
  font-size: 1rem; font-weight: 800; color: #0f172a; margin-bottom: 4px;
`;
const StepDesc = styled.div`
  font-size: 0.83rem; color: #64748b; line-height: 1.55;
`;

/* ── Study group cards ── */
const StudyCard = styled(Card)`display: flex; flex-direction: column; padding: 0;`;
const StudyHeader = styled.div`
  padding: 22px 22px 12px; display: flex; align-items: flex-start; gap: 12px;
`;
const GroupIcon = styled.div`
  width: 48px; height: 48px; border-radius: 12px;
  background: ${({ $color }) => `linear-gradient(135deg, ${$color}, ${$color}dd)`};
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 1rem; font-weight: 900; flex-shrink: 0;
`;
const StudyTitle = styled.div`font-size: 1.05rem; font-weight: 800; color: #0f172a; line-height: 1.35;`;
const StudyMeta = styled.div`font-size: 0.82rem; color: #64748b; margin-top: 4px;`;
const StudyBody = styled.div`padding: 0 22px 16px; font-size: 0.88rem; color: #475569; line-height: 1.6; flex: 1;`;
const StudyFoot = styled.div`
  padding: 14px 22px; border-top: 1px solid #f1f5f9;
  display: flex; justify-content: space-between; align-items: center; font-size: 0.83rem;
`;
const Capacity = styled.span`color: ${({ $full }) => $full ? '#dc2626' : '#16a34a'}; font-weight: 700;`;
const JoinBtn = styled.button`
  background: ${({ theme }) => theme.colors.primary[700]};
  color: #fff; -webkit-text-fill-color: #fff;
  border: none; border-radius: 8px; padding: 8px 14px;
  font-size: 0.83rem; font-weight: 800; cursor: pointer;
  &:disabled { background: #cbd5e1; cursor: not-allowed; }
`;

/* ── Roadmap ── */
const RoadmapWrap = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 22px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const RoadmapCard = styled(Card)`
  padding: 28px 28px 24px;
  border-top: 5px solid ${({ $color }) => $color};
`;
const RoadmapHeader = styled.div`display: flex; align-items: center; gap: 14px; margin-bottom: 18px;`;
const RoadmapIcon = styled.div`
  width: 56px; height: 56px; border-radius: 14px;
  background: ${({ $color }) => `${$color}1A`};
  color: ${({ $color }) => $color};
  display: flex; align-items: center; justify-content: center;
  font-size: 1.4rem; font-weight: 900;
`;
const RoadmapTitle = styled.div`font-size: 1.2rem; font-weight: 900; color: #0f172a; line-height: 1.25;`;
const RoadmapSub = styled.div`font-size: 0.85rem; color: #64748b; margin-top: 4px;`;
const Steps = styled.ol`list-style: none; padding: 0; margin: 16px 0 0; counter-reset: step;`;
const Step = styled.li`
  display: grid; grid-template-columns: 30px 1fr; gap: 14px;
  padding: 12px 0; border-top: 1px dashed #e2e8f0; counter-increment: step;
  &:first-child { border-top: none; padding-top: 6px; }
  &::before {
    content: counter(step);
    width: 26px; height: 26px; border-radius: 50%;
    background: ${({ $color }) => `${$color}1A`};
    color: ${({ $color }) => $color};
    font-weight: 900; font-size: 0.82rem;
    display: flex; align-items: center; justify-content: center;
  }
`;
const StepBody = styled.div`
  .name { font-size: 0.95rem; font-weight: 800; color: #0f172a; }
  .desc { font-size: 0.83rem; color: #64748b; margin-top: 2px; line-height: 1.55; }
`;
const RoadmapFoot = styled.div`
  margin-top: 18px; padding-top: 16px; border-top: 1px solid #f1f5f9;
  display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap;
`;
const RoadmapBadgeLine = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;
const RoadmapCta = styled(Link)`
  text-decoration: none;
  background: ${({ $color }) => $color};
  color: #fff; -webkit-text-fill-color: #fff;
  border-radius: 8px; padding: 9px 16px; font-size: 0.85rem; font-weight: 800;
`;

/* ── Section anchors ── */
const SectionAnchor = styled.div`
  position: relative;
  &::before { content: ''; display: block; height: 60px; margin-top: -60px; visibility: hidden; pointer-events: none; }
`;

/* ────────────────────────────────────────────────
   data
   ──────────────────────────────────────────────── */

const WORKSPACES = [
  { color: '#2563eb', icon: 'WK', title: '작업관리',  desc: '프로젝트 태스크와 진행 상황을 체계적으로 관리합니다.', link: '/Developer', cta: '작업실 입장 →' },
  { color: '#059669', icon: 'PF', title: '포트폴리오', desc: '완성된 프로젝트와 작업 결과물을 포트폴리오로 정리합니다.', link: '/Portfolio', cta: '포트폴리오 보기 →' },
  { color: '#d97706', icon: 'CA', title: '일정관리',  desc: '프로젝트 일정과 마일스톤을 캘린더로 관리합니다.', link: '/WorkCalendar', cta: '일정 관리 →' },
];

const ROLES = [
  { color: '#2563eb', icon: 'DV', title: '개발자',     desc: '웹/앱 개발 프로젝트, 코드 리뷰, 이슈 트래킹', link: '/Developer' },
  { color: '#ec4899', icon: 'DG', title: '디자이너',   desc: 'UI/UX 디자인 작업, 시안 관리, 피드백 수집',   link: '/Designer' },
  { color: '#059669', icon: 'IF', title: '인프라',     desc: '서버/네트워크 인프라 관리, 배포 및 모니터링', link: '/Infra' },
  { color: '#7c3aed', icon: 'MD', title: '모델러',     desc: '3D 모델링 프로젝트, 리소스 관리',           link: '/Modeler' },
  { color: '#dc2626', icon: 'MV', title: '미디어',     desc: '영상/사진 콘텐츠 제작 및 편집 관리',         link: '/Media' },
  { color: '#0891b2', icon: 'PL', title: '기획',       desc: '프로젝트 기획, 요구사항 분석, 일정 계획',     link: '/Plan' },
];

const STEPS = [
  { num: 'STEP 01', title: '프로젝트 생성', desc: '새 프로젝트를 만들고 팀원을 초대합니다.' },
  { num: 'STEP 02', title: '태스크 분배',   desc: '역할별로 업무를 나누고 일정을 설정합니다.' },
  { num: 'STEP 03', title: '진행 관리',     desc: '실시간으로 진행 상황을 추적하고 소통합니다.' },
  { num: 'STEP 04', title: '포트폴리오',    desc: '완성된 결과물을 포트폴리오로 정리합니다.' },
];

const STUDY_GROUPS = [
  { id: 1, color: '#0d9488', icon: 'RE', title: 'React 18 실전 스터디',  topic: 'Frontend',  desc: '훅·상태관리·성능 최적화까지 4주 코드리뷰 중심으로 진행.', members: 8,  capacity: 12, schedule: '매주 화 19:30 / 온라인' },
  { id: 2, color: '#2563eb', icon: 'CT', title: '코딩테스트 매일 1문제',  topic: 'Algorithm', desc: '백준·프로그래머스 매일 풀고 풀이를 공유합니다.',           members: 24, capacity: 30, schedule: '매일 / Slack 인증' },
  { id: 3, color: '#7c3aed', icon: 'AI', title: 'LLM 사이드 프로젝트',    topic: 'AI',        desc: 'OpenAI/Anthropic API로 작은 SaaS 만들기. 6주.',          members: 5,  capacity: 8,  schedule: '매주 토 14:00 / 강남 오프라인' },
  { id: 4, color: '#dc2626', icon: 'BE', title: 'Node.js 백엔드 정복',    topic: 'Backend',   desc: 'Express + PostgreSQL 정통 백엔드 구축. 책 완독.',         members: 12, capacity: 15, schedule: '매주 목 20:00 / 온라인' },
  { id: 5, color: '#0891b2', icon: 'AW', title: 'AWS 자격증 합격 스터디', topic: 'DevOps',    desc: 'SAA 자격증 준비. 모의고사·핵심 개념 정리.',                members: 7,  capacity: 10, schedule: '매주 일 10:00 / 온라인' },
  { id: 6, color: '#9333ea', icon: 'UX', title: '디자인 시스템 리서치',    topic: 'Design',    desc: '주요 회사 디자인 시스템 분석/공유, Figma 라이브러리.',     members: 4,  capacity: 6,  schedule: '격주 수 19:00 / 온라인' },
];

const ROADMAPS = [
  {
    color: '#2563eb', icon: 'FE', title: '프론트엔드 개발자', sub: '실무 React/TypeScript 중심',
    badges: ['React', 'TypeScript', 'Next.js'],
    steps: [
      { name: 'JS/TS 핵심 문법',     desc: 'ES6+ 자바스크립트와 타입스크립트 기본기 정복' },
      { name: '실무 React 마스터',    desc: '훅, 상태관리, 성능 최적화' },
      { name: '컴포넌트 설계 패턴',   desc: '재사용 컴포넌트와 디자인 시스템 구축' },
      { name: 'Next.js + 배포',      desc: 'SSR/ISR, Vercel 배포까지 한 사이클 경험' },
    ],
  },
  {
    color: '#0d9488', icon: 'BE', title: '백엔드 개발자', sub: 'Node.js + PostgreSQL 정통',
    badges: ['Node.js', 'PostgreSQL', 'REST API'],
    steps: [
      { name: 'Node.js & Express',         desc: '비동기 모델, 미들웨어, REST 라우팅 설계' },
      { name: 'PostgreSQL 데이터 모델링',   desc: '관계 설계·인덱스·트랜잭션' },
      { name: '인증·보안·세션',             desc: 'bcrypt, JWT, SSO 통합 인증 패턴' },
      { name: '배포 & 모니터링',           desc: 'Docker, pm2, 로그·메트릭 수집' },
    ],
  },
  {
    color: '#7c3aed', icon: 'AI', title: 'AI 엔지니어', sub: 'LLM 응용 서비스 개발',
    badges: ['LLM', 'RAG', 'LangChain'],
    steps: [
      { name: 'AI 애플리케이션 엔지니어링', desc: 'LLM API, 프롬프트 디자인 기초' },
      { name: 'RAG 파이프라인',             desc: '벡터 DB, 임베딩, 컨텍스트 관리' },
      { name: '에이전트 워크플로우',         desc: '도구 호출, 멀티스텝 자동화' },
      { name: '프로덕션 배포',               desc: '비용·레이턴시 최적화, 평가/모니터링' },
    ],
  },
  {
    color: '#dc2626', icon: 'OP', title: 'DevOps 엔지니어', sub: '클라우드 운영·자동화',
    badges: ['Docker', 'Kubernetes', 'AWS'],
    steps: [
      { name: 'Linux & 네트워크 기본기',  desc: 'CLI, 네트워크, 권한 관리' },
      { name: 'Docker & 컨테이너',        desc: '이미지 빌드·레지스트리·로컬 오케스트레이션' },
      { name: 'CI/CD 파이프라인',         desc: 'GitHub Actions, 자동 배포' },
      { name: 'AWS / 클라우드 운영',       desc: 'EC2, RDS, S3, IAM, 모니터링' },
    ],
  },
];

export default function WorkRoomPage() {
  const [joinedIds, setJoinedIds] = useState({});

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.style.height = 'auto';
    html.style.overflowY = 'auto';
    body.style.height = 'auto';
    body.style.overflowY = 'auto';
    return () => {
      html.style.height = '';
      html.style.overflowY = '';
      body.style.height = '';
      body.style.overflowY = '';
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const join = (g) => {
    if (g.members >= g.capacity) return;
    setJoinedIds((s) => ({ ...s, [g.id]: true }));
    alert(`'${g.title}' 스터디 신청이 접수되었습니다.`);
  };

  return (
    <PageWrapper>
      <Hero>
        <HeroOrb $size="280px" $top="-60px" $right="-40px" />
        <HeroOrb $size="160px" $bottom="-40px" $left="10%" />
        <HeroInner>
          <HeroEyebrow>WORK ROOM</HeroEyebrow>
          <HeroTitle>작업실</HeroTitle>
          <HeroDesc>
            직무별 협업 공간 · 포트폴리오 · 일정관리부터 스터디 그룹과 추천 학습 로드맵까지,
            성장에 필요한 모든 도구를 한 곳에서 만나보세요.
          </HeroDesc>
        </HeroInner>
      </Hero>

      <Anchors>
        <AnchorsInner>
          <AnchorBtn onClick={() => scrollTo('workspaces')}>워크스페이스</AnchorBtn>
          <AnchorBtn onClick={() => scrollTo('roles')}>직무별 작업실</AnchorBtn>
          <AnchorBtn onClick={() => scrollTo('workflow')}>작업 흐름</AnchorBtn>
          <AnchorBtn onClick={() => scrollTo('study')}>스터디 그룹</AnchorBtn>
          <AnchorBtn onClick={() => scrollTo('roadmap')}>추천 로드맵</AnchorBtn>
        </AnchorsInner>
      </Anchors>

      {/* 1. 워크스페이스 */}
      <SectionAnchor id="workspaces">
        <Section>
          <Inner>
            <SectionHeader>
              <Divider $center />
              <SectionTitle>워크스페이스</SectionTitle>
              <SectionSubtitle>작업관리 · 포트폴리오 · 일정관리 - 핵심 작업 공간</SectionSubtitle>
            </SectionHeader>
            <WorkspaceGrid>
              {WORKSPACES.map((w) => (
                <WorkspaceCard key={w.title} to={w.link} $color={w.color}>
                  <WSIcon $color={w.color}>{w.icon}</WSIcon>
                  <WSTitle>{w.title}</WSTitle>
                  <WSDesc>{w.desc}</WSDesc>
                  <WSCta $color={w.color}>{w.cta}</WSCta>
                </WorkspaceCard>
              ))}
            </WorkspaceGrid>
          </Inner>
        </Section>
      </SectionAnchor>

      {/* 2. 직무별 작업실 */}
      <SectionAnchor id="roles">
        <Section style={{ background: '#f8fafc' }}>
          <Inner>
            <SectionHeader>
              <Divider $center />
              <SectionTitle>직무별 작업실</SectionTitle>
              <SectionSubtitle>역할에 맞는 전용 게시판에서 작업물·이슈·자료를 관리합니다</SectionSubtitle>
            </SectionHeader>
            <RoleGrid>
              {ROLES.map((r) => (
                <RoleCard key={r.title} to={r.link} $color={r.color}>
                  <RoleIcon $color={r.color}>{r.icon}</RoleIcon>
                  <RoleBody>
                    <RoleTitle>{r.title}</RoleTitle>
                    <RoleDesc>{r.desc}</RoleDesc>
                  </RoleBody>
                </RoleCard>
              ))}
            </RoleGrid>
          </Inner>
        </Section>
      </SectionAnchor>

      {/* 3. 작업 흐름 */}
      <SectionAnchor id="workflow">
        <Section>
          <Inner>
            <SectionHeader>
              <Divider $center />
              <SectionTitle>작업 흐름</SectionTitle>
              <SectionSubtitle>프로젝트 시작부터 완료까지 4단계 워크플로우</SectionSubtitle>
            </SectionHeader>
            <Workflow>
              {STEPS.map((s) => (
                <StepCard key={s.num}>
                  <StepNum>{s.num}</StepNum>
                  <StepTitle>{s.title}</StepTitle>
                  <StepDesc>{s.desc}</StepDesc>
                </StepCard>
              ))}
            </Workflow>
          </Inner>
        </Section>
      </SectionAnchor>

      {/* 4. 스터디 그룹 */}
      <SectionAnchor id="study">
        <Section style={{ background: '#f8fafc' }}>
          <Inner>
            <SectionHeader>
              <Divider $center />
              <SectionTitle>활성 스터디 그룹</SectionTitle>
              <SectionSubtitle>같은 관심사 수강생들과 함께 공부하세요. 모집 중인 그룹은 바로 신청 가능합니다.</SectionSubtitle>
            </SectionHeader>
            <Grid $cols={3} $colsTablet={2} $colsMobile={1}>
              {STUDY_GROUPS.map((g) => {
                const full = g.members >= g.capacity;
                const joined = !!joinedIds[g.id];
                return (
                  <StudyCard key={g.id}>
                    <StudyHeader>
                      <GroupIcon $color={g.color}>{g.icon}</GroupIcon>
                      <div>
                        <Badge $variant={full ? 'default' : 'success'}>{full ? '모집마감' : '모집중'}</Badge>
                        <StudyTitle style={{ marginTop: 6 }}>{g.title}</StudyTitle>
                        <StudyMeta>{g.topic} · {g.schedule}</StudyMeta>
                      </div>
                    </StudyHeader>
                    <StudyBody>{g.desc}</StudyBody>
                    <StudyFoot>
                      <Capacity $full={full}>{g.members}/{g.capacity}명</Capacity>
                      <JoinBtn onClick={() => join(g)} disabled={full || joined}>
                        {joined ? '신청 완료' : full ? '마감' : '신청하기'}
                      </JoinBtn>
                    </StudyFoot>
                  </StudyCard>
                );
              })}
            </Grid>
          </Inner>
        </Section>
      </SectionAnchor>

      {/* 5. 추천 로드맵 */}
      <SectionAnchor id="roadmap">
        <Section>
          <Inner>
            <SectionHeader>
              <Divider $center />
              <SectionTitle>직무별 추천 학습 로드맵</SectionTitle>
              <SectionSubtitle>목표 직무에 맞춰 어떤 과정을 어떤 순서로 들어야 할지 안내해 드립니다</SectionSubtitle>
            </SectionHeader>
            <RoadmapWrap>
              {ROADMAPS.map((r) => (
                <RoadmapCard key={r.title} $color={r.color}>
                  <RoadmapHeader>
                    <RoadmapIcon $color={r.color}>{r.icon}</RoadmapIcon>
                    <div>
                      <RoadmapTitle>{r.title}</RoadmapTitle>
                      <RoadmapSub>{r.sub}</RoadmapSub>
                    </div>
                  </RoadmapHeader>
                  <Steps>
                    {r.steps.map((s) => (
                      <Step key={s.name} $color={r.color}>
                        <StepBody>
                          <div className="name">{s.name}</div>
                          <div className="desc">{s.desc}</div>
                        </StepBody>
                      </Step>
                    ))}
                  </Steps>
                  <RoadmapFoot>
                    <RoadmapBadgeLine>
                      {r.badges.map((b) => <Badge key={b}>{b}</Badge>)}
                    </RoadmapBadgeLine>
                    <RoadmapCta to="/Courses" $color={r.color}>전체 과정 보기 →</RoadmapCta>
                  </RoadmapFoot>
                </RoadmapCard>
              ))}
            </RoadmapWrap>
          </Inner>
        </Section>
      </SectionAnchor>
    </PageWrapper>
  );
}
